// Watch List Widget Columns - Minimal 3-line display for dashboard with DYNAMIC league stats
import React from 'react';
import { dynastyTheme } from '../colorService';
import { getStatConfigs, formatStatValue, getStatValue, DEFAULT_BATTING_STATS, DEFAULT_PITCHING_STATS } from '../../utils/statMapping';
import { excludeStaticStatConfigs, STATIC_HITTING_FIELDS, STATIC_PITCHING_FIELDS } from '../../utils/statColumnDedup';
import InjuryBadge from '../../components/InjuryBadge';
import PlayerLink from '../../components/PlayerLink';

/**
 * Create columns for watch list widget - simplified for dashboard with league scoring categories
 * @param {Object} league - League object with scoring_categories
 * @param {boolean} isPitcher - Whether to show pitcher or hitter stats
 * @returns {Array} Column configurations
 */
export const createWatchListWidgetColumns = (league = null, isPitcher = false) => {
  // Determine which stats to show based on league scoring categories
  const cats = league?.league_settings?.scoring_categories;
  const statLabels = cats
    ? (isPitcher
        ? (cats.pitching || DEFAULT_PITCHING_STATS)
        : (cats.hitting || DEFAULT_BATTING_STATS)
      )
    : (isPitcher ? DEFAULT_PITCHING_STATS : DEFAULT_BATTING_STATS);

  // DD-STAT-CONSOLIDATION sweep: DEFAULT_*_STATS include the very context stats
  // (G/AB/H, G/GS/IP) this factory renders as static columns → drop the dynamic
  // duplicates at the field level so nothing renders twice.
  const statConfigs = excludeStaticStatConfigs(
    getStatConfigs(statLabels, isPitcher),
    isPitcher ? STATIC_PITCHING_FIELDS : STATIC_HITTING_FIELDS
  );

  const columns = [
    // COLUMN 1: Position
    {
      key: 'position',
      title: 'Pos',
      width: 40,
      render: (player, rowType, slotInfo) => (
        <span className={dynastyTheme.classes.text.neutralLight}>
          {player?.info?.position || '-'}
        </span>
      )
    },
    // COLUMN 2: Player Name
    {
      key: 'player_name',
      title: 'Player',
      width: 120,
      render: (player, rowType, slotInfo) => {
        // Season row - show player name in white
        if (rowType === 'season') {
          return (
            <span className="inline-flex items-center">
              <PlayerLink
                mlbId={player?.ids?.mlb}
                name={`${player?.info?.first_name || ''} ${player?.info?.last_name || ''}`.trim()}
                style={{ fontSize: '0.875rem', fontWeight: 500, color: '#f9fafb' }}
              />
              <InjuryBadge mlbId={player?.ids?.mlb} />
            </span>
          );
        } else {
          // Rolling row - show stat type label
          return (
            <div className="text-left text-[10px] font-normal opacity-50 text-yellow-400">
              ↗ 14-Day
            </div>
          );
        }
      }
    },
    // COLUMN 3: Team
    {
      key: 'team',
      title: 'Team',
      width: 50,
      render: (player, rowType, slotInfo) => (
        <span className={dynastyTheme.classes.text.neutralLight}>
          {player?.info?.mlb_team || 'FA'}
        </span>
      )
    },
    // COLUMN 4: Games
    {
      key: 'games',
      title: 'G',
      width: 40,
      render: (player, rowType, slotInfo) => {
        let value;
        if (rowType === 'season') {
          value = isPitcher ? player?.stats?.season?.pitching?.games_played : player?.stats?.season?.hitting?.games_played;
        } else if (rowType === 'rolling') {
          value = isPitcher ? player?.stats?.rolling_14_day?.pitching?.games_played : player?.stats?.rolling_14_day?.hitting?.games_played;
        } else {
          value = isPitcher ? player?.stats?.active_accrued?.pitching?.games_played : player?.stats?.active_accrued?.hitting?.games_played;
        }
        const textColor = rowType === 'season' ? dynastyTheme.classes.text.neutralLight : 'text-yellow-400';
        return <span className={textColor}>{value || 0}</span>;
      }
    }
  ];

  // COLUMNS 5 & 6: Position-specific stats
  if (isPitcher) {
    // PITCHERS: GS, IP
    columns.push(
      {
        key: 'games_started',
        title: 'GS',
        width: 40,
        render: (player, rowType) => {
          let value;
          if (rowType === 'season') {
            value = player?.stats?.season?.pitching?.games_started;
          } else if (rowType === 'rolling') {
            value = player?.stats?.rolling_14_day?.pitching?.games_started;
          } else {
            value = player?.stats?.active_accrued?.pitching?.games_started;
          }
          const textColor = rowType === 'season' ? dynastyTheme.classes.text.neutralLight : 'text-yellow-400';
          return <span className={textColor}>{value || 0}</span>;
        }
      },
      {
        key: 'innings_pitched',
        title: 'IP',
        width: 50,
        render: (player, rowType) => {
          let value;
          if (rowType === 'season') {
            value = player?.stats?.season?.pitching?.innings_pitched;
          } else if (rowType === 'rolling') {
            value = player?.stats?.rolling_14_day?.pitching?.innings_pitched;
          } else {
            value = player?.stats?.active_accrued?.pitching?.innings_pitched;
          }
          const textColor = rowType === 'season' ? dynastyTheme.classes.text.neutralLight : 'text-yellow-400';
          return <span className={textColor}>{value?.toFixed(1) || '0.0'}</span>;
        }
      }
    );
  } else {
    // HITTERS: AB, H
    columns.push(
      {
        key: 'at_bats',
        title: 'AB',
        width: 40,
        render: (player, rowType) => {
          let value;
          if (rowType === 'season') {
            value = player?.stats?.season?.hitting?.at_bats;
          } else if (rowType === 'rolling') {
            value = player?.stats?.rolling_14_day?.hitting?.at_bats;
          } else {
            value = player?.stats?.active_accrued?.hitting?.at_bats;
          }
          const textColor = rowType === 'season' ? dynastyTheme.classes.text.neutralLight : 'text-yellow-400';
          return <span className={textColor}>{value || 0}</span>;
        }
      },
      {
        key: 'hits',
        title: 'H',
        width: 40,
        render: (player, rowType) => {
          let value;
          if (rowType === 'season') {
            value = player?.stats?.season?.hitting?.hits;
          } else if (rowType === 'rolling') {
            value = player?.stats?.rolling_14_day?.hitting?.hits;
          } else {
            value = player?.stats?.active_accrued?.hitting?.hits;
          }
          const textColor = rowType === 'season' ? dynastyTheme.classes.text.neutralLight : 'text-yellow-400';
          return <span className={textColor}>{value || 0}</span>;
        }
      }
    );
  }

  // COLUMNS 7+: DYNAMIC league scoring category columns
  statConfigs.forEach((config) => {
    // config.field is e.g. 'hitting.batting_avg' — extract just the field name
    // since statCategory already provides the hitting/pitching prefix
    const fieldName = config.field.includes('.') ? config.field.split('.').pop() : config.field;

    columns.push({
      key: config.field,
      title: config.label,
      width: 50,
      render: (player, rowType, slotInfo) => {
        let value;
        const statCategory = isPitcher ? 'pitching' : 'hitting';

        if (rowType === 'season') {
          value = player?.stats?.season?.[statCategory]?.[fieldName];
        } else if (rowType === 'rolling') {
          value = player?.stats?.rolling_14_day?.[statCategory]?.[fieldName];
        } else {
          value = player?.stats?.active_accrued?.[statCategory]?.[fieldName];
        }
        
        // Color: white for season, yellow for rolling
        const textColor = rowType === 'season' ? dynastyTheme.classes.text.neutralLight : 'text-yellow-400';
        
        return (
          <span className={textColor}>
            {formatStatValue(value, config)}
          </span>
        );
      }
    });
  });

  return columns;
};
